import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Divider,
} from '@mui/material';
import { Schedule, KeyboardArrowDown } from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { useAtom } from 'jotai';
import settingsAtom from '../../atoms/settings/SettingsAtom';

dayjs.extend(utc);
dayjs.extend(timezone);

const SettingsPage = () => {
  const [selectedSetting, setSelectedSetting] = useState('timezone');
  // eslint-disable-next-line
  const [settingsData, setSettingsData] = useAtom(settingsAtom);
  
  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    const stored = localStorage.getItem('timezone_preference');
    return stored || Intl.DateTimeFormat().resolvedOptions().timeZone;
  });

  const projectId = localStorage.getItem('project_id');

  const currentProject = settingsData.projectList?.find(proj => proj.id.toString() === projectId) || null;

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  const projectTimezone = currentProject?.timezone || browserTimezone;

  const getTimezoneOptions = () => {
    const options = [];

    if (projectTimezone && projectTimezone !== browserTimezone && currentProject?.timezone) {
      options.push({
        value: projectTimezone,
        label: `Project Timezone (${projectTimezone})`,
        type: 'project'
      });
    }

    options.push({
      value: browserTimezone,
      label: `Browser Local Timezone (${browserTimezone})`,
      type: 'browser'
    });

    return options;
  };

  const timezoneOptions = getTimezoneOptions();

  useEffect(() => {
    localStorage.setItem('timezone_preference', selectedTimezone);
  }, [selectedTimezone]);

  const handleTimezoneChange = (event) => {
    const newTimezone = event.target.value;
    setSelectedTimezone(newTimezone);
  };

  const getCurrentTimezoneInfo = () => {
    const selectedOption = timezoneOptions.find(option => option.value === selectedTimezone);
    if (selectedOption) {
      return {
        timezone: selectedOption.value,
        label: selectedOption.label,
        type: selectedOption.type
      };
    }
    
    return {
      timezone: selectedTimezone,
      label: `Custom Timezone (${selectedTimezone})`,
      type: 'custom'
    };
  };

  const formatTimeInTimezone = (timezone) => {
    try {
      return new Date().toLocaleString('en-US', {
        timeZone: timezone,
        dateStyle: 'full',
        timeStyle: 'medium'
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Invalid timezone';
    }
  };

  const renderSettingContent = () => {
    switch (selectedSetting) {
      case 'timezone':
        const currentTimezoneInfo = getCurrentTimezoneInfo();
        
        return (
          <Card sx={{ maxWidth: 800 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Timezone Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure your timezone preferences for the application. This affects how dates and times are displayed throughout the system.
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="timezone-select-label">Timezone</InputLabel>
                <Select
                  labelId="timezone-select-label"
                  id="timezone-select"
                  value={selectedTimezone}
                  label="Timezone"
                  onChange={handleTimezoneChange}
                  IconComponent={KeyboardArrowDown}
                >
                  {timezoneOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ 
                p: 2, 
                backgroundColor: 'grey.50', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200',
                mb: 2
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Currently Selected:</strong> {currentTimezoneInfo.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Active Timezone:</strong> {currentTimezoneInfo.timezone}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Current Time in Selected Timezone:</strong> {formatTimeInTimezone(currentTimezoneInfo.timezone)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Type:</strong> {currentTimezoneInfo.type === 'browser' ? 'Browser Local Time' : 
                    currentTimezoneInfo.type === 'project' ? 'Project Time' : 'Custom'}
                </Typography>
              </Box>

            </CardContent>
          </Card>
        );
      default:
        return (
          <Card sx={{ maxWidth: 600 }}>
            <CardContent>
              <Typography variant="h6">Settings</Typography>
              <Typography variant="body2" color="text.secondary">
                Select a setting from the sidebar to configure.
              </Typography>
            </CardContent>
          </Card>
        );
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Paper 
        elevation={1} 
        sx={{ 
          width: 280, 
          borderRadius: 0,
          borderRight: 1,
          borderColor: 'divider'
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Settings
          </Typography>
        </Box>
        <Divider />
        
        <List sx={{ p: 0 }}>
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedSetting === 'timezone'}
              onClick={() => setSelectedSetting('timezone')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon>
                <Schedule />
              </ListItemIcon>
              <ListItemText primary="Timezone" />
            </ListItemButton>
          </ListItem>
        </List>
      </Paper>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Settings
        </Typography>
        {renderSettingContent()}
      </Box>
    </Box>
  );
};

export default SettingsPage;